import type { ReactNode } from "react";
import { EmptyState } from "@cntrl/ui";
import { useBandContextOrNull } from "../../app/bandContext.js";
import styles from "./AntennaHubScreen.module.scss";

/**
 * The route-level launch gate for the band-side Antenna hub (plan §1.3 AC-1, D-12). The SERVER is the gate:
 * a band whose hub is not open gets no `antenna:*` token, so its nav offers no Antenna and every hub route
 * 403s. This is the page half — what `/antenna` (and its routed modals) render when someone arrives anyway:
 * by URL, from an old bookmark, or through the roster's Antenna tab, which stays keyed on the subview so the
 * tab strip keeps one shape across artists (NavigationDocument.BuildRosterTabs).
 *
 * It wraps the hub AND its `<Outlet/>`, so on a closed band no hub query fires and no modal mounts — which is
 * also why the modals' own "not available for your role" states never speak for a closed hub: that sentence
 * is about the seat, this one is about the band. Absent context fails closed.
 */
export function AntennaHubGate({ children }: { children: ReactNode }) {
  const band = useBandContextOrNull();
  if (band?.antennaHubEnabled) return <>{children}</>;
  return <AntennaHubNotOpen roster={band?.shell === "roster"} />;
}

/** The designed not-open state. Calm, not an error: nobody did anything wrong, and there is nothing to fix. */
function AntennaHubNotOpen({ roster }: { roster: boolean }) {
  return (
    <section className={styles.screen} aria-label="Antenna">
      <EmptyState
        title={roster ? "Antenna isn't open for this artist yet" : "Antenna isn't open for your band yet"}
        description="Antenna, the fan club, is opening to artists in stages. The hub appears here as soon as it's switched on — there's nothing to set up in the meantime."
      />
    </section>
  );
}
